import axios from 'axios';
import jwtDecode from 'jwt-decode';
import EventType from '../enums/EventTypeEnum';
import { clearOktaStorage } from './okta.auth';

// A separate axios instance, so this call does not pick up the global interceptor and send
// our headers to ipapi.co. A custom header on another site's URL causes a CORS preflight
// that would fail.
const geoAxios = axios.create();

const AuthService = {
  // Finds the user's location here in the browser, because only the browser sees the real
  // IP address. The server is behind a load balancer and sees the balancer instead.
  //
  // If it fails, nothing is stored and the log shows "unknown". Signing in is not affected.
  async resolveClientLocation() {
    try {
      const { data } = await geoAxios.get('https://ipapi.co/json');

      if (data?.city && data?.country_name) {
        sessionStorage.setItem('clientLocation', `${data.city}, ${data.country_name}`);
      }
    } catch {
      // Ignored on purpose.
    }
  },

  getClientLocation() {
    return sessionStorage.getItem('clientLocation');
  },

  isAuthenticated() {
    const identity = JSON.parse(sessionStorage.getItem('userIdentity'));

    if (!identity?.token) {
      return false;
    }

    const token = jwtDecode(identity.token);
    const isTokenExpired = token.exp * 1000 < Date.now();

    if (isTokenExpired) {
      sessionStorage.removeItem('userIdentity');
      localStorage.setItem(EventType.Flush_Credentials, Date.now().toString());
      localStorage.removeItem(EventType.Flush_Credentials);
    }

    return !isTokenExpired;
  },

  setUserIdentity(userIdentity) {
    sessionStorage.setItem('userIdentity', JSON.stringify(userIdentity));
    window.localStorage.setItem(EventType.Share_Credentials, JSON.stringify(userIdentity));
    window.localStorage.removeItem(EventType.Share_Credentials);
  },

  /**
   * Persists Op2mise session identity from a Login / OktaLogin response body.
   * Uses the same shape password login already expects (T15: provisional sessionStorage).
   */
  applyLoginSession(responseData, { authProvider } = {}) {
    const userIdentity = {
      userId: responseData.userId,
      roleId: responseData.roleId,
      organisationId: responseData.organisationId,
      token: responseData.token,
    };
    if (authProvider) {
      userIdentity.authProvider = authProvider;
    }
    this.setUserIdentity(userIdentity);
    return userIdentity;
  },

  /**
   * Clears any partial Op2mise session identity without calling the logout API.
   * Used when OktaLogin (or similar) rejects after a half-applied session.
   */
  clearLoginSession() {
    sessionStorage.removeItem('userIdentity');
    clearOktaStorage();
    localStorage.setItem(EventType.Flush_Credentials, Date.now().toString());
    localStorage.removeItem(EventType.Flush_Credentials);
  },

  getUserIdentity() {
    const userIdentity = JSON.parse(sessionStorage.getItem('userIdentity'));
    return userIdentity;
  },

  login(loginInfo) {
    const url = `${process.env.REACT_APP_HOST_URL}Auth/Login`;
    return axios.post(url, loginInfo);
  },

  async oktaLoginAsync({ idToken }) {
    const url = `${process.env.REACT_APP_HOST_URL}Auth/OktaLogin`;
    const response = await axios.post(url, { idToken });
    return response;
  },

  logout() {
    const userIdentity = JSON.parse(sessionStorage.getItem('userIdentity'));
    if (userIdentity && userIdentity.userId !== null) {
      const url = `${process.env.REACT_APP_HOST_URL}auth/LogoutUser?ID=${userIdentity.userId}`;
      axios.post(url).then(() => {
        sessionStorage.removeItem('userIdentity');
      });
    }

    clearOktaStorage();
    localStorage.setItem(EventType.Flush_Credentials, Date.now().toString());
    localStorage.removeItem(EventType.Flush_Credentials);
  },

  getToken() {
    const userIdentity = JSON.parse(sessionStorage.getItem('userIdentity'));
    if (!userIdentity) return null;
    return userIdentity.token;
  },

  getRememberedCredentials() {
    const credentials = JSON.parse(localStorage.getItem('credentials'));
    if (credentials) {
      return credentials.filter((o) => o.email && o.ssid); // filter out invalid credentials
    }
    return [];
  },

  getRememberedCredential(email) {
    const credentials = this.getRememberedCredentials();
    const credential = credentials.find((o) => o.email === email);

    return credential;
  },

  rememberCredential(credential) {
    let credentials = JSON.parse(localStorage.getItem('credentials'));
    const duplicate = this.getRememberedCredential(credential.email);

    if (duplicate) {
      const first = credential.email;
      credentials.sort((x, y) => {
        let retVal = 0;
        if (x.email === first) {
          retVal = -1;
        } else if (y.email === first) {
          retVal = 1;
        }
        return retVal;
      });

      localStorage.setItem('credentials', JSON.stringify(credentials));

      return false;
    }

    if (credentials) {
      credentials.unshift(credential);
      localStorage.setItem('credentials', JSON.stringify(credentials));
    } else {
      credentials = [];
      credentials.push(credential);
      localStorage.setItem('credentials', JSON.stringify(credentials));
    }
    return true;
  },

  removeCredential(credential) {
    const credentials = JSON.parse(localStorage.getItem('credentials'));

    if (!credentials) {
      return false;
    }

    const filteredCredentials = [
      ...credentials.filter((o) => o.email !== credential.email),
    ];
    const removed = filteredCredentials.length !== credentials.length;

    // prettier-ignore
    if (filteredCredentials.length > 0) {
      localStorage.setItem(
        'credentials',
        JSON.stringify(filteredCredentials),
      );
    } else {
      localStorage.removeItem('credentials');
    }

    return removed;
  },

  isOrganisationalAdmin() {
    const user = JSON.parse(sessionStorage.getItem('userIdentity'));

    return user.organisationId;
  },

  setSelectedSchedule(selectedSchedule) {
    localStorage.setItem('selectedSchedule', JSON.stringify(selectedSchedule));
  },

  getSelectedSchedule() {
    // prettier-ignore
    const selectedSchedule = JSON.parse(
      localStorage.getItem('selectedSchedule'),
    );
    return selectedSchedule;
  },
  setSelectedSubmission(selectedSubmission) {
    localStorage.setItem(
      'selectedSubmission',
      JSON.stringify(selectedSubmission),
    );
  },

  getSelectedSubmission() {
    // prettier-ignore
    const selectedSubmission = JSON.parse(
      localStorage.getItem('selectedSubmission'),
    );
    return selectedSubmission;
  },
};
export default AuthService;
